/**
 * @module experience/byod/compare-devices
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HARDGOODS_CONSTANTS } from '@experience/shop/hardgoods/constants/browse-hardgoods.constants';
import { BrowseHardgoodsService } from '@experience/shop/hardgoods/services/browse-hardgoods.service';
import { Subject } from 'rxjs';
import { Filter } from '@support/api/common/product.interface';
import { BrowseDeviceModel } from '@experience/shop/hardgoods/models/browse-device.model';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { takeUntil } from 'rxjs/operators';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { HardgoodsDeviceDetails } from '@experience/shop/hardgoods/interfaces/browse-hardgood.interface';
import { PDLBaseComponent } from '@experience/reb-pdl/components/pdl.base.component';
import { PdlService } from '@experience/reb-pdl/services/pdl.service';
import { AemAuthorableData } from '@core/aem/interfaces/legal-text/aem-authorable.interface';
import { HARDGOODS_MODULE_COMPONENTS } from '@experience/shop/hardgoods/constants/hardgoods.constants';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';

@Component({
  selector: 'tmo-browse-accessory',
  templateUrl: './browse-accessory.component.html',
  styleUrls: [
    '../../../../../../style-sheets/components/page-titles.component.scss',
    '../../../../../../style-sheets/components/input-checkbox.component.scss',
  ],
})
export class BrowseAccessoryComponent extends PDLBaseComponent implements OnInit, OnDestroy {
  public showComponentSpinner: boolean;
  /**
   * Un-subscriber for all subscriptions
   */
  private ngUnsubscribe: Subject<void>;
  /**
   * Stores the filters
   */
  public filters: Filter[];
  /**
   * Stores hardgoods data in a model
   */
  public hardGoods: BrowseDeviceModel[];
  /**
   * Stores the total accessory count
   */
  public accessoryCount: number;
  /**
   * Stores AEM data
   */
  public aemConfigurableData: ConfigData;
  /**
   * Stores the boolean value of show see more link
   */
  public showSeeMore: boolean;

  /**
   * Stores value of legal text authorable filtered object
   */
  public legalText: AemAuthorableData[];
  /**
   * AEM Authorable Content for BYOD Landing Page
   */
  public authorableData: AemAuthorableData[];

  constructor(
    private router: Router,
    private browseHardgoodsService: BrowseHardgoodsService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private cartSupportService: CartSupportService,
    private objectService: ObjectService,
    public pdlService: PdlService,
    private activatedRoute: ActivatedRoute
  ) {
    super(pdlService, activatedRoute);
    this.ngUnsubscribe = new Subject<void>();
    this.showSeeMore = true;
  }

  /**
   * ngOnInit to initialize the component data
   */
  public ngOnInit(): void {
    super.ngOnInit();
    const radix = 10;
    const accessoryCount = HARDGOODS_CONSTANTS.ACCESSORY_COUNT;
    this.accessoryCount = parseInt(accessoryCount, radix);
    this.loadAemContent();
    this.browseHardgoodsService.hardGoodDataObservable$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((hardgoodData: HardgoodsDeviceDetails) => {
        this.hardGoods = this.objectService.get(hardgoodData, 'productModels');
        this.showSeeMore = this.stateStoreUtilsService.getShowSeeMore();
        this.showComponentSpinner = false;
      });
    this.browseHardgoodsService.loadAcccessoryCategorySubject.next(false);
  }

  /**
   * ngOnDestroy to clear the variables when component scope destroys
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Makes API call after reaching to bottem of page to get next 24 values
   */
  public navigateToCdp(category: string): void {
    this.router.navigate([HARDGOODS_CONSTANTS.BROWSE_ACCESSORY_CATEGORY_DETAILS], {
      queryParams: { category: category },
    });
  }

  /**
   * On scroll method when use wants to see more results
   */
  public onScroll(): void {
    this.showComponentSpinner = true;
    this.browseHardgoodsService.loadAcccessoryCategorySubject.next(true);
  }

  /**
   * Load AEM content for Accessories
   * @returns void
   */
  private loadAemContent(): void {
    this.cartSupportService
      .getAEMContent('browse-accessories')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((aemData: ConfigData) => {
        if (aemData) {
          this.aemConfigurableData = this.objectService.get(aemData, 'configurableData');
          this.authorableData = this.objectService.get(aemData, 'authorableData') || [];
          this.legalText = this.authorableData.filter((legalValue: AemAuthorableData) => {
            return legalValue.uiComponent === HARDGOODS_MODULE_COMPONENTS.LEGAL_TEXT;
          });
        }
      });
  }
}
